import { FaMapMarkerAlt, FaThermometerHalf, FaTruck, FaWrench, FaDoorOpen } from 'react-icons/fa'
import { PiEngineFill, PiMapPinAreaFill } from 'react-icons/pi'

import type { AssetState } from '@/types/asset-types'

import DetailCard from './DetailCard'
import useAssetDetailItems from './useAssetDetailItems'
import { formatZoneTemp } from './utils'

export interface AssetOverviewProps {
  asset: AssetState
  companyId: number | undefined
  units: 'Imperial' | 'Metric'
}

/**
 * Everything about the asset's current state on one screen, in three columns: where it is
 * and what the reefer is doing; zones, monitoring points and doors (a reefer has at most 3
 * zones, but can have 10+ monitoring points, so they list vertically); then engine and
 * maintenance. Collapses to two columns, then one, as the container narrows. Every column
 * stretches to the tallest one, and its cards share the extra height evenly, so they all
 * end on the same line.
 */
export default function AssetOverview({ asset, companyId, units }: AssetOverviewProps) {
  const {
    locationDetails,
    additionalDetails,
    reeferDetails,
    engineDetails,
    maintenanceDetails,
    monitoringPointDetails,
    doorDetails,
    sections,
  } = useAssetDetailItems(asset, companyId, units)
  const powerOn = asset.powerOn
  const showMiddleColumn = sections.zones || sections.monitoringPoints || sections.doors
  const showRightColumn = sections.engineAndMaintenance
  const gridCols =
    showMiddleColumn && showRightColumn
      ? '@5xl:grid-cols-[3fr_4fr_3fr]'
      : showMiddleColumn || showRightColumn
        ? '@5xl:grid-cols-2'
        : '@5xl:grid-cols-1'

  return (
    <div className={`grid grid-cols-1 @2xl:grid-cols-2 ${gridCols} gap-2`}>
      <div className="flex flex-col gap-2 min-w-0">
        <DetailCard
          dense
          className="grow"
          icon={<FaMapMarkerAlt />}
          title={asset.gatewayOn ? 'Location' : 'Last Known Location'}
          items={locationDetails}
        />
        {sections.reefer && (
          <DetailCard dense className="grow" icon={<FaTruck />} title="Reefer" items={reeferDetails} />
        )}
      </div>

      {showMiddleColumn && (
        <div className="flex flex-col gap-2 min-w-0">
          {sections.zones && (
            <DetailCard
              dense
              className="grow"
              title="Zones"
              icon={<PiMapPinAreaFill />}
              children={
                asset.zones.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="table table-xs w-full">
                      <thead>
                        <tr>
                          <th className="px-1"></th>
                          <th className="px-1">Set Point</th>
                          <th className="px-1">Virtual SP</th>
                          <th className="px-1">Return Air</th>
                          <th className="px-1">Supply Air</th>
                          <th className="px-1">Mode</th>
                        </tr>
                      </thead>
                      <tbody>
                        {asset.zones.map((zone, idx) => (
                          <tr key={idx}>
                            <td className="px-1">{idx + 1}</td>
                            <td className="px-1">{powerOn ? formatZoneTemp(zone.setPoint, units) : ''}</td>
                            <td className="px-1">{powerOn ? formatZoneTemp(zone.virtualSetPoint, units) : ''}</td>
                            <td className="px-1">{powerOn ? formatZoneTemp(zone.returnAir, units) : ''}</td>
                            <td className="px-1">{powerOn ? formatZoneTemp(zone.supplyAir, units) : ''}</td>
                            <td className="px-1">{powerOn ? (zone.mode ?? '') : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-xs text-base-content/60">No zones</div>
                )
              }
            />
          )}
          {sections.monitoringPoints && (
            <DetailCard
              dense
              className="grow"
              icon={<FaThermometerHalf />}
              title="Temperature Monitoring Points"
              items={monitoringPointDetails}
            />
          )}
          {sections.doors && (
            <DetailCard dense className="grow" icon={<FaDoorOpen />} title="Doors" items={doorDetails} />
          )}
        </div>
      )}

      {showRightColumn && (
        <div className="flex flex-col gap-2 min-w-0">
          <DetailCard
            dense
            className="grow"
            icon={<PiEngineFill />}
            title="Engine"
            items={[
              ...engineDetails,
              // A small gap sets the additional reefer data apart from the hours.
              ...additionalDetails.map((item, idx) => (idx === 0 ? { ...item, className: 'mt-3' } : item)),
            ]}
          />
          <DetailCard
            dense
            className="grow"
            icon={<FaWrench />}
            title="Maintenance & Service"
            items={maintenanceDetails}
          />
        </div>
      )}
    </div>
  )
}
